import { type TouchEvent, useRef, useState } from 'react'
import { IconChevronLeft, IconChevronRight } from './icons'

/**
 * A swipeable image gallery. Shows one image at a time; with more than one it adds prev/next
 * arrows, dot indicators, and touch-swipe. Controlled via a translateX track (no scroll listener)
 * so the active dot and arrows stay in sync. Renders nothing if there are no images.
 */
export function ImageCarousel({ images, alt = '' }: { images: string[]; alt?: string }) {
  const pics = images.filter(Boolean)
  const [index, setIndex] = useState(0)
  const startX = useRef<number | null>(null)

  if (pics.length === 0) return null

  const go = (i: number) => setIndex(Math.max(0, Math.min(pics.length - 1, i)))

  const onTouchStart = (e: TouchEvent) => {
    startX.current = e.touches[0]?.clientX ?? null
  }
  const onTouchEnd = (e: TouchEvent) => {
    if (startX.current === null) return
    const dx = (e.changedTouches[0]?.clientX ?? startX.current) - startX.current
    if (Math.abs(dx) > 40) go(index + (dx < 0 ? 1 : -1))
    startX.current = null
  }

  const multiple = pics.length > 1

  return (
    <div className="carousel" onTouchStart={onTouchStart} onTouchEnd={onTouchEnd}>
      <div className="carousel-track" style={{ transform: `translateX(-${index * 100}%)` }}>
        {pics.map((src, i) => (
          <img
            key={`${src}-${i}`}
            className="carousel-slide"
            src={src}
            alt={i === 0 ? alt : ''}
            draggable={false}
            loading={i === 0 ? 'eager' : 'lazy'}
          />
        ))}
      </div>

      {multiple && (
        <>
          <button
            type="button"
            className="carousel-nav prev"
            aria-label="Previous image"
            disabled={index === 0}
            onClick={() => go(index - 1)}
          >
            <IconChevronLeft />
          </button>
          <button
            type="button"
            className="carousel-nav next"
            aria-label="Next image"
            disabled={index === pics.length - 1}
            onClick={() => go(index + 1)}
          >
            <IconChevronRight />
          </button>
          <div className="carousel-count num" aria-hidden>
            {index + 1} / {pics.length}
          </div>
        </>
      )}
    </div>
  )
}
